import { IAction, IActionDefinitionDto, IBluetoothCharacteristic, IMethodInput } from "@kuika/kuika-cl-model";
import _ from "lodash";
import { DatasourceService } from "../../../services/datasource-service";
import { PartialContextProvider } from "../../../shared/patial/partial-context-provider";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { normalizeMethodInputTypes } from "../../../shared/utility/method-input-type-normalization";
import { IScreenDesignContextState } from "../screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "../screen-design-context-provider";

export interface BluetoothMethodProviderState {
  editingBluetoothMethodId: string | undefined;
  setEditingBluetoothMethod: (methodId: string | undefined) => void;
  cancelEditingBluetoothMethod: () => void;
  setActiveBluetoothMethod: (methodId: string | undefined) => void;
  cancelActiveBluetoothMethod: () => void;
  activeBluetoothMethodId?: string;
  getSelectedBluetoothMethod: () => IActionDefinitionDto | undefined;
  updateSelectedBluetoothMethodActions: (actions: IAction[]) => void;
  addActionToBluetoothMethod: (action: IAction) => void;
  deleteActionOfBluetoothMethod: (action: IAction) => void;
  createBluetoothMethodInput: (methodInput: IMethodInput) => void;
  updateBluetoothMethodInput: (methodInput: IMethodInput) => void;
  deleteBluetoothMethodInput: (methodInput: IMethodInput) => void;
}

export class BluetoothMethodProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  BluetoothMethodProviderState
> {
  static defaultState: BluetoothMethodProviderState = {
    editingBluetoothMethodId: undefined,
    setEditingBluetoothMethod: () => {},
    cancelEditingBluetoothMethod: () => {},
    setActiveBluetoothMethod: () => {},
    cancelActiveBluetoothMethod: () => {},
    activeBluetoothMethodId: undefined,
    getSelectedBluetoothMethod: () => undefined,
    updateSelectedBluetoothMethodActions: () => {},
    addActionToBluetoothMethod: () => {},
    deleteActionOfBluetoothMethod: () => {},
    createBluetoothMethodInput: () => {},
    updateBluetoothMethodInput: () => {},
    deleteBluetoothMethodInput: () => {}
  };

  getProviderValues = (): BluetoothMethodProviderState => {
    return {
      editingBluetoothMethodId: this.state.editingBluetoothMethodId,
      setEditingBluetoothMethod: this.setEditingBluetoothMethod,
      cancelEditingBluetoothMethod: this.cancelEditingBluetoothMethod,
      setActiveBluetoothMethod: this.setActiveBluetoothMethod,
      cancelActiveBluetoothMethod: this.cancelActiveBluetoothMethod,
      activeBluetoothMethodId: this.state.activeBluetoothMethodId,
      getSelectedBluetoothMethod: this.getSelectedBluetoothMethod,
      updateSelectedBluetoothMethodActions: this.updateSelectedBluetoothMethodActions,
      addActionToBluetoothMethod: this.addActionToBluetoothMethod,
      deleteActionOfBluetoothMethod: this.deleteActionOfBluetoothMethod,
      createBluetoothMethodInput: this.createBluetoothMethodInput,
      updateBluetoothMethodInput: this.updateBluetoothMethodInput,
      deleteBluetoothMethodInput: this.deleteBluetoothMethodInput
    };
  };

  setEditingBluetoothMethod = (methodId: any) => {
    this.setState({ editingBluetoothMethodId: methodId });
  };

  cancelEditingBluetoothMethod = () => {
    this.setState({ editingBluetoothMethodId: undefined });
  };

  setActiveBluetoothMethod = (methodId: any) => {
    this.setState({ activeBluetoothMethodId: methodId });
  };

  cancelActiveBluetoothMethod = () => {
    this.setState({ activeBluetoothMethodId: undefined });
  };

  getSelectedBluetoothMethod = (): IActionDefinitionDto | undefined => {
    const method = _.cloneDeep(this.state.actionDefinitions.find((a) => a.id === this.state.activeBluetoothMethodId));
    if (!method || !method.bluetoothInfo) return undefined;
    const bluetoothInfo: IBluetoothCharacteristic = JSON.parse(method.bluetoothInfo as unknown as string);
    // Read-repair the legacy numeric EDataType on method-input types; every designer consumer of a
    // Bluetooth method reaches it through here, and the write paths below re-serialize what they
    // read, so a repaired method is also persisted repaired on its next save.
    normalizeMethodInputTypes(bluetoothInfo.inputs);
    return { ...method, bluetoothInfo };
  };

  updateSelectedBluetoothMethodActions = (actions: IAction[]) => {
    const actionDefinitions = _.cloneDeep(this.state.actionDefinitions);
    const bleMethod = actionDefinitions.find((a) => a.id === this.state.activeBluetoothMethodId);

    if (!bleMethod?.bluetoothInfo) return;
    const bluetoothInfo = JSON.parse(bleMethod.bluetoothInfo as any);
    bluetoothInfo.actions = [...actions];
    bleMethod.bluetoothInfo = JSON.stringify(bluetoothInfo) as any;
    this.setState({ actionDefinitions });
  };

  addActionToBluetoothMethod = (action: IAction) => {
    const method = _.cloneDeep(this.getSelectedBluetoothMethod());
    if (!method || !method.bluetoothInfo) return;
    const currentActionIndex = method.bluetoothInfo.actions.findIndex((a) => a.id === action.id);
    if (currentActionIndex > -1) {
      method.bluetoothInfo.actions.splice(currentActionIndex, 1, action);
    } else {
      method.bluetoothInfo.actions.push(action);
    }
    this.saveAction(method);
  };

  deleteActionOfBluetoothMethod = (action: IAction) => {
    const method = _.cloneDeep(this.getSelectedBluetoothMethod());
    if (!method || !method.bluetoothInfo || !method.bluetoothInfo.actions.length) return;

    const deleteActionRecursively = (actions: IAction[], actionToDelete: IAction): boolean => {
      const actionIndex = actions.findIndex((a) => a.id === actionToDelete.id);

      if (actionIndex >= 0) {
        actions.splice(actionIndex, 1);
        return true;
      }

      for (let i = 0; i < actions.length; i++) {
        if (actions[i].description === "IfThenElse" && actions[i].ifThenElseData) {
          if (actions[i].ifThenElseData?.ifActions && Array.isArray(actions[i].ifThenElseData?.ifActions)) {
            const ifActions = actions[i].ifThenElseData!.ifActions;
            if (deleteActionRecursively(ifActions, actionToDelete)) {
              return true;
            }
          }

          if (actions[i].ifThenElseData?.elseActions && Array.isArray(actions[i].ifThenElseData?.elseActions)) {
            const elseActions = actions[i].ifThenElseData!.elseActions;
            if (deleteActionRecursively(elseActions, actionToDelete)) {
              return true;
            }
          }
        }
        if (
          actions[i].description === "ForEach" &&
          actions[i]?.forEachData?.actions &&
          Array.isArray(actions[i]?.forEachData?.actions)
        ) {
          const forEachActions = actions[i]?.forEachData?.actions as IAction[];
          if (deleteActionRecursively(forEachActions, actionToDelete)) {
            return true;
          }
        }
      }

      return false;
    };

    deleteActionRecursively(method.bluetoothInfo.actions, action);
    this.saveAction(method);
  };

  createBluetoothMethodInput = (methodInput: IMethodInput) => {
    const method = _.cloneDeep(this.getSelectedBluetoothMethod());
    if (method && method.bluetoothInfo && methodInput) {
      const exists = method.bluetoothInfo.inputs?.find((inputMethod) => inputMethod.name === methodInput.name);
      if (exists) {
        KMainFunctions.displayWarningNotification("Method input with this name is already exists.");
      } else {
        if (method.bluetoothInfo.inputs?.length === 0) method.bluetoothInfo.inputs = [];
        method.bluetoothInfo.inputs.push(methodInput);
        this.saveAction(method);
      }
    }
  };

  updateBluetoothMethodInput = (methodInput: IMethodInput) => {
    const method = _.cloneDeep(this.getSelectedBluetoothMethod());

    if (method && method.bluetoothInfo && methodInput) {
      const exists = method?.bluetoothInfo.inputs?.find(
        (inputMethod) => inputMethod.name === methodInput.name && inputMethod.id !== methodInput.id
      );

      if (exists) {
        KMainFunctions.displayWarningNotification("Method input with this name is already exists.");
        return;
      }

      if (!method.bluetoothInfo.inputs) method.bluetoothInfo.inputs = [];
      const currentActionIndex = method?.bluetoothInfo.inputs.findIndex((a) => a.id == methodInput.id);
      if (currentActionIndex > -1) {
        method.bluetoothInfo.inputs[currentActionIndex] = methodInput;
      }
      this.saveAction(method);
    }
  };

  deleteBluetoothMethodInput = (methodInput: IMethodInput) => {
    const method = _.cloneDeep(this.getSelectedBluetoothMethod());

    if (method && method.bluetoothInfo && methodInput) {
      if (!method.bluetoothInfo.inputs) method.bluetoothInfo.inputs = [];
      const currentMethodIndex = method?.bluetoothInfo.inputs.findIndex((a) => a.id == methodInput.id);
      if (currentMethodIndex > -1) {
        method.bluetoothInfo.inputs.splice(currentMethodIndex, 1);
      }
      this.saveAction(method);
    }
  };

  private saveAction = (method: IActionDefinitionDto) => {
    method.parameters = [];
    method.bluetoothInfo = JSON.stringify(method.bluetoothInfo) as any;
    DatasourceService.SaveAction(method)
      .then((response) => {
        this.setState({ actionDefinitions: response.data });
      })
      .catch((error) => {
        KMainFunctions.exceptionHandler(error);
      });
  };
}
